import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { dsoApi } from '../services/api';

// Component for handling business loan documents based on business type
const BusinessLoanDocuments = ({ leadId, businessType, isLeadLocked }) => {
  console.log("BusinessLoanDocuments MOUNTED:", { leadId, businessType, isLeadLocked });
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState({});
  const [uploadError, setUploadError] = useState({});
  const [requiredDocs, setRequiredDocs] = useState({
    required_documents: [],
    optional_documents: []
  });

  // Fetch documents and required document list on load
  useEffect(() => {
    if (leadId && businessType) {
      console.log("BusinessLoanDocuments mounted with:", leadId, businessType);
      fetchDocuments();
      fetchRequiredDocuments();
    }
  }, [leadId, businessType]);

  // Fetch loan documents for this lead
  const fetchDocuments = async () => {
    setLoading(true);
    try {
      console.log("Fetching loan documents for lead:", leadId);
      
      try {
        const response = await dsoApi.getLoanDocuments(leadId);
        console.log("Loan documents response (plural):", response);
        setDocuments(response.data || {});
      } catch (pluralErr) {
        console.log("Error fetching loan documents, creating new record:", pluralErr);
        
        // If the document doesn't exist (404), create it by uploading an initial document
        // First, create the document record by uploading a placeholder file
        const formData = new FormData();
        
        // Create a small blank file
        const emptyBlob = new Blob([''], { type: 'text/plain' });
        const emptyFile = new File([emptyBlob], 'placeholder.txt', { type: 'text/plain' });
        
        formData.append('file', emptyFile);
        formData.append('document_type', 'kyc_file');  // Use a standard field name instead of additional_documents
        formData.append('business_type', businessType);
        
        console.log("Creating initial loan document record for business type:", businessType);
        
        // try {
        //   await dsoApi.uploadLoanDocument(leadId, formData);
        //   console.log("Successfully created initial loan document record");
          
        //   // Now try fetching the document record again
        //   const newResponse = await dsoApi.getLoanDocuments(leadId);
        //   console.log("Fresh loan documents response:", newResponse);
        //   setDocuments(newResponse.data || {});
        // } catch (createErr) {
        //   console.error("Failed to create initial loan document record:", createErr);
        //   throw createErr; // Rethrow to be caught by the outer catch
        // }
      }
    } catch (err) {
      console.error('Error handling loan documents:', err);
      setError('Failed to initialize loan documents. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch required documents list based on business type
  const fetchRequiredDocuments = async () => {
    try {
      const response = await dsoApi.getRequiredLoanDocuments(businessType);
      setRequiredDocs(response.data || { required_documents: [], optional_documents: [] });
    } catch (err) {
      console.error('Error fetching required documents:', err);
    }
  };

  // Upload document
  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [docType]: true }));
    setUploadError(prev => ({ ...prev, [docType]: null }));

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    formData.append('business_type', businessType);

    // For partner/director documents, add additional info
    if (docType === 'partners_documents' || docType === 'directors_documents') {
      const additionalInfo = JSON.stringify({
        index: 0, // Default to first partner/director
        doc_type: 'kyc_file',
        name: 'Partner/Director 1'
      });
      formData.append('additional_info', additionalInfo);
    }

    try {
      await dsoApi.uploadLoanDocument(leadId, formData);
      // Refresh documents
      fetchDocuments();
    } catch (error) {
      console.error(`Error uploading ${docType}:`, error);
      setUploadError(prev => ({ 
        ...prev, 
        [docType]: error.response?.data?.detail || `Failed to upload ${file.name}`
      }));
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  if (loading) {
    return (
      <div className="text-center my-4">
        <Spinner animation="border" variant="primary" />
        <p>Loading documents...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <div className="d-flex align-items-center">
          <FaExclamationTriangle className="me-2" /> 
          <div>
            <p className="mb-0">{error}</p>
          </div>
        </div>
      </Alert>
    );
  }

  // Define document fields based on business type
  let documentFields = [];
  
  // Common documents for all business types
  const commonFields = [
    { key: 'resident_address_proof_file', label: 'Resident Address Proof (Electricity Bill)' },
    { key: 'office_address_proof_file', label: 'Office Address Proof' },
    { key: 'gumasta_udyam_certificate_file', label: 'Gumasta, Udyam Certificate' },
    { key: 'itr_ca_attested_file', label: 'Last 2 Years ITR CA Attested' },
    { key: 'gst_certificate_file', label: 'GST Certificate' },
    { key: 'bank_statements_file', label: 'Current A/C Statements PDF' },
    { key: 'business_vintage_proof_file', label: 'Business Vintage Proof' },
    { key: 'gst_r3b_file', label: 'GST R3B' },
    { key: 'company_pan_card_file', label: 'Company PAN Card' },
    { key: 'loan_obligation_sheet_file', label: 'Loan Obligation Sheet' }
  ];
  
  // Specific documents for each business type
  if (businessType === 'Partnership Firm') {
    documentFields = [
      ...commonFields,
      { key: 'partners_documents', label: 'All Partner KYC and Passport Photo' },
      { key: 'partnership_deed_file', label: 'Partnership Deed' },
      { key: 'detailsheet_file', label: 'Detailsheet' }
    ];
  } else if (businessType === 'Pvt Ltd Company') {
    documentFields = [
      ...commonFields,
      { key: 'directors_documents', label: 'All Director KYC and Passport Photo' },
      { key: 'moa_aao_file', label: 'MOA & AAO' },
      { key: 'share_holding_pattern_file', label: 'Share Holding Pattern' }
    ];
  } else if (businessType === 'Proprietorship') {
    documentFields = [
      ...commonFields,
      { key: 'co_app_kyc_file', label: 'Co-app KYC with Photo' },
      { key: 'ownership_proof_file', label: 'Ownership Proof' }
    ];
  }

  return (
    <>
      <h4 className="mb-3" style={{color: '#0056b3'}}>Business Loan Documents ({businessType})</h4>
      
      {documentFields.map((field, index) => (
        <Row key={index} className="mb-3">
          <Col md={12}>
            <Form.Group>
              <Form.Label>
                {field.label}
                {requiredDocs.required_documents.includes(field.key) && (
                  <span className="text-danger ms-1">*</span>
                )}
              </Form.Label>
              <div className="d-flex align-items-center">
                <Form.Control
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(e) => handleDocumentUpload(e, field.key)}
                  className={documents[field.key] ? "border-success" : ""}
                  disabled={isLeadLocked || uploading[field.key]}
                />
                {documents[field.key] && (
                  <Button 
                    variant="link" 
                    href={documents[field.key]} 
                    target="_blank" 
                    className="ms-2"
                  >
                    View
                  </Button>
                )}
              </div>

              {uploading[field.key] && (
                <div className="mt-2">
                  <Spinner animation="border" size="sm" /> Uploading...
                </div>
              )}

              {uploadError[field.key] && (
                <div className="text-danger mt-1 small">
                  {uploadError[field.key]}
                </div>
              )}

              {documents[field.key] && (
                <div className="d-flex align-items-center text-success mt-1 small">
                  <FaCheckCircle className="me-1" /> Document uploaded
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>
      ))}

      {!documentFields.length && (
        <Alert variant="info">
          Please select a business type to see required documents.
        </Alert>
      )}
    </>
  );
};

export default BusinessLoanDocuments;